import { getCore } from '../../../client/core/getCore';
import type { DynamicCoreState } from '../../../client/core/types';
import type { DynamicClient } from '../../../client/types';
import { SDK_API_CORE_VERSION } from '../../../constants';
import { checkAndRaiseWalletAccountsChangedEvent } from '../../auth/updateAuthFromVerifyResponse/checkAndRaiseWalletAccountsChangedEvent';
import { sessionStorageKeySchema, stateStorageKeySchema } from '../schema';

export const hydrateStateWithStorage = async (
  client: DynamicClient
): Promise<void> => {
  const core = getCore(client);

  const previousState = core.state.get();

  const stateChanges: Partial<DynamicCoreState> = {};

  // ================ TOKEN =================

  const session = await core.storage.getItem(sessionStorageKeySchema);

  const isSessionValid =
    session?.sessionExpiration && session.sessionExpiration > Date.now();

  if (isSessionValid) {
    stateChanges.token = session.token;
    stateChanges.legacyToken = session.legacyToken;
    stateChanges.sessionExpiresAt = new Date(session.sessionExpiration);
    stateChanges.mfaToken = session.mfaToken;
    stateChanges.elevatedAccessTokens = session.elevatedAccessTokens.map((token) => ({
      expiresAt: new Date(token.expiresAt),
      scopes: token.scopes,
      singleUse: token.singleUse ?? false,
      token: token.token,
    }));
    stateChanges.captchaToken = session.captchaToken;
    stateChanges.sessionKeys = session.sessionKeys;
  }

  // ================ STATE =================

  const storedState = await core.storage.getItem(stateStorageKeySchema);

  // Only hydrate state if it came from an API with the same version as the current SDK
  if (storedState?.apiVersion === SDK_API_CORE_VERSION) {
    stateChanges.unverifiedWalletAccounts =
      storedState.unverifiedWalletAccounts;

    stateChanges.walletProviderKeyMap = storedState.walletProviderKeyMap;

    if (storedState.prefetchedNoncesExpiration && storedState.prefetchedNoncesExpiration > Date.now()) {
      stateChanges.prefetchedNonces = storedState.prefetchedNonces;
      stateChanges.prefetchedNoncesExpiration = storedState.prefetchedNoncesExpiration;
    }

    // Variables that should only be hydrated if the session is valid
    if (isSessionValid) {
      stateChanges.user = storedState.user;
    }

    // if there's a session/user or connected unverified wallets, we should hydrate project settings
    // so we don't refetch it on initial load
    if (
      isSessionValid ||
      (storedState.unverifiedWalletAccounts ?? []).length > 0
    ) {
      stateChanges.projectSettings = storedState.projectSettings;
    }
  }

  if (Object.keys(stateChanges).length > 0) {
    core.state.set(stateChanges);
  }

  checkAndRaiseWalletAccountsChangedEvent({ previousState }, client);
};
